/**
 * Id generation, ported from legacy `src/utils/functions.ts`.
 *
 * Two consumers: the MQTT client id (SYM-489) and the stable per-component ids that key
 * the store's `messages` / `topicItems` maps (SYM-491). The latter is why the output format
 * matters — see `generateStringId`.
 */

/**
 * UUID v4, lowercase, dash-separated.
 *
 * `crypto.randomUUID()` when it exists. It does not always: it is restricted to secure
 * contexts, and these displays are LAN-deployed devices that may well be served over plain
 * `http://`, where the method is simply absent. For that case the legacy hand-rolled
 * generator is kept as a fallback — its output format is identical, so ids stay comparable
 * with the legacy app's logs during SYM-506 parity testing.
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return legacyUUID();
}

/**
 * The legacy generator, ported verbatim from `utils/functions.ts` (public domain / MIT).
 *
 * It seeds from `Date.now()` and then from `performance.now()` microseconds, mixing both
 * into `Math.random()`. Not cryptographically strong — but it never was, and its only job
 * here is uniqueness within one page, where it has been adequate for years on the legacy
 * displays.
 */
function legacyUUID(): string {
  let d = new Date().getTime();
  let d2 = (typeof performance !== 'undefined' && performance.now() * 1000) || 0;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let r = Math.random() * 16;
    if (d > 0) {
      r = ((d + r) % 16) | 0;
      d = Math.floor(d / 16);
    } else {
      r = ((d2 + r) % 16) | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * `'s' + uuid-without-dashes + 's'`.
 *
 * The `s` bookends are legacy: these ids were Vuex module names and had to be valid
 * identifiers (docs/MIGRATION.md §8). They are plain map keys now, but the shape is
 * harmless and keeping it makes new and legacy logs directly comparable.
 */
export function generateStringId(): string {
  return 's' + generateUUID().replaceAll('-', '') + 's';
}
